/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
  Chip,
  Breadcrumbs,
  Link,
  IconButton,
  Collapse,
  Tabs,
  Tab
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonIcon from '@mui/icons-material/Person';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import EventIcon from '@mui/icons-material/Event';
import GavelIcon from '@mui/icons-material/Gavel';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ITopic, ITopicExplanation, getAllTopics, getTopicById, searchTopics, getTopicExplanations } from '../services/topicService';
import { getDebateById } from '../services/debateService';
import { getUserById } from '../services/userService';
import ChallengeExplanationModal from '../components/ChallengeExplanationModal';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

// Formats date to a nice readable format
const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  return format(date, 'MMM d, yyyy');
};



// Topic card component
const TopicCard: React.FC<{ 
  topic: ITopic; 
  onClick: () => void;
}> = ({ topic, onClick }) => {
  const hasParent = !!topic.parentTopic;
  const hasChildren = topic.childTopics.length > 0;
  
  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6
        }
      }}
      onClick={onClick}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom>{topic.name}</Typography>
        {topic.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            {topic.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {hasParent && <Chip size="small" label="Sub-topic" color="primary" sx={{ mr: 1 }} />}
            {hasChildren && <Chip size="small" label={`${topic.childTopics.length} Sub-topics`} />}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {topic.explanations.length} explanation{topic.explanations.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Explanation card component
const ExplanationCard: React.FC<{
  explanation: ITopicExplanation;
  expanded: boolean;
  toggleExpand: () => void;
  onChallenge?: () => void;
}> = ({ explanation, expanded, toggleExpand, onChallenge }) => {
  const [debateInfo, setDebateInfo] = useState<any>(null);
  const [username, setUsername] = useState('');
  
  // Load debate info when component mounts
  useEffect(() => {
    const loadDebateInfo = async () => {
      try {
        const debate = await getDebateById(explanation.debateId);
        setDebateInfo({
          topic: debate.topic,
          url: `/debate/${explanation.debateId}`,
          teams: debate.teams || []
        });
      } catch (error) {
        logger.error('Error loading debate info:', error);
      }
    };
    
    const loadUsername = async () => {
      try {
          const user = await getUserById(explanation.providedBy);
        setUsername(user.username || 'Unknown');
        } catch (error) {
          logger.error('Error loading username:', error);
        setUsername('Unknown');
      }
    };
    
    loadDebateInfo();
    loadUsername();
  }, [explanation.debateId, explanation.providedBy]);
  
  // Calculate challenge survival metrics
  const challengesSurvived = explanation.challengesSurvived || 0;
  const challengesReceived = explanation.challengesReceived || 0;
  const survivalRate = challengesReceived > 0 ? (challengesSurvived / challengesReceived) * 100 : 100;
  const openCriticisms = explanation.openCriticisms?.filter(c => !c.addressed).length || 0;
  const status = explanation.status || 'active';
  
  // Status color and icon
  const getStatusDisplay = () => {
    switch (status) {
      case 'active':
        return { color: 'success.main', icon: '🟢', label: 'CUBE Explanation' };
      case 'under_challenge':
        return { color: 'warning.main', icon: '🟡', label: 'Under Challenge' };
      case 'challenged':
        return { color: 'error.main', icon: '🔴', label: 'Currently not in CUBE' };
      default:
        return { color: 'text.secondary', icon: '⚪', label: 'Unknown' };
    }
  };
  
  const statusDisplay = getStatusDisplay();
  
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <FormatQuoteIcon color="primary" sx={{ mr: 1 }} />
            
            {/* Status Display */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 0.5 }}>
                {statusDisplay.icon}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: statusDisplay.color, fontWeight: 'medium' }}
              >
                {statusDisplay.label}
              </Typography>
            </Box>
            
            {/* Challenge Survival Metrics */}
            {challengesReceived > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Survival: {survivalRate.toFixed(0)}% ({challengesSurvived}/{challengesReceived})
                </Typography>
              </Box>
            )}
            
            {/* Open Criticisms */}
            {openCriticisms > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  color="warning"
                  onClick={() => {
                    // Find the first unaddressed criticism and navigate to its source debate
                    const firstCriticism = explanation.openCriticisms?.find(c => !c.addressed);
                    if (firstCriticism?.sourceDebateId) {
                      window.open(`/debate/view/${firstCriticism.sourceDebateId}`, '_blank');
                    }
                  }}
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                >
                  {openCriticisms} open criticism{openCriticisms !== 1 ? 's' : ''}
                </Button>
              </Box>
            )}
            
            {/* Version indicator for updated explanations */}
            {explanation.version && explanation.version > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  • v{explanation.version}
            </Typography>
              </Box>
            )}
          </Box>
          
          <IconButton onClick={toggleExpand} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Typography variant="body1" paragraph>
            {explanation.text}
          </Typography>
        </Collapse>
        
        {!expanded && (
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
            {explanation.text}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography 
              component={RouterLink} 
              to={`/user/${explanation.providedBy}`} 
              variant="body2"
              sx={{ 
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {username}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">{formatDate(explanation.addedAt)}</Typography>
          </Box>
        </Box>
        
        {debateInfo && (
          <Box sx={{ mt: 1 }}>
            <Button 
              component={RouterLink} 
              to={debateInfo.url} 
              variant="outlined" 
              size="small" 
              sx={{ mt: 1, mr: 1 }}
            >
              View Source Debate
            </Button>
            
            {/* Challenge Button */}
            {onChallenge && (
              <Button
                variant="contained"
                size="small"
                color="warning"
                startIcon={<GavelIcon />}
                sx={{ mt: 1 }}
                onClick={onChallenge}
              >
                Challenge
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Main CUBE Page component
const CubePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [topics, setTopics] = useState<ITopic[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<ITopic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<ITopic | null>(null);
  const [explanations, setExplanations] = useState<ITopicExplanation[]>([]);
  const [expandedExplanations, setExpandedExplanations] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [activeParent, setActiveParent] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTopics, setHasMoreTopics] = useState(true);
  const [totalTopics, setTotalTopics] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const TOPICS_PER_PAGE = 15;
  
  // Challenge modal state
  const [challengeModalOpen, setChallengeModalOpen] = useState(false);
  const [selectedExplanationForChallenge, setSelectedExplanationForChallenge] = useState<{
    explanation: ITopicExplanation;
    index: number;
  } | null>(null);
  
  // Load initial topics on component mount
  useEffect(() => {
    loadInitialTopics();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount
  }, []);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Check if we're going back from a topic view
      const state = event.state;
      if (!state || !state.topicSelected) {
        // We're going back to the topics list view
        if (selectedTopic) {
          setSelectedTopic(null);
          setExplanations([]);
          setActiveParent(null);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedTopic]);

  const loadInitialTopics = async () => {
    try {
      setIsLoading(true);
      setCurrentPage(1);
      const topicsData = await getAllTopics();
      
      // For now, we'll still get all topics but implement client-side pagination
      // In a real app, you'd want server-side pagination with: getAllTopics(page, limit, sortBy)
      setTopics(topicsData);
      setTotalTopics(topicsData.length);
      
      // Apply initial filtering and pagination
      const initialTopics = getFilteredAndPaginatedTopics(topicsData, 1, 0);
      setFilteredTopics(initialTopics);
      setHasMoreTopics(1 * TOPICS_PER_PAGE < topicsData.length);
      
      setIsLoading(false);
    } catch (error) {
      logger.error('Error loading topics:', error);
      setIsLoading(false);
    }
  };

  const getFilteredAndPaginatedTopics = (allTopics: ITopic[], page: number, tabIndex: number) => {
    let sortedTopics = [...allTopics];
    
    // Apply sorting based on tab
    if (tabIndex === 0) {
      // All Topics - sort alphabetically
      sortedTopics.sort((a, b) => a.name.localeCompare(b.name));
    } else if (tabIndex === 1) {
      // Recent - sort by creation date (most recent first)
      sortedTopics.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });
    }
    
    // Apply pagination
    const startIndex = 0; // Always start from beginning for "Load More" pattern
    const endIndex = page * TOPICS_PER_PAGE;
    return sortedTopics.slice(startIndex, endIndex);
  };

  // Handle tab changes and update filtered topics
  useEffect(() => {
    if (searchQuery.trim()) {
      // Don't override search results
      return;
    }
    
    // Reset pagination when tab changes
    setCurrentPage(1);
    const newFilteredTopics = getFilteredAndPaginatedTopics(topics, 1, tabValue);
    setFilteredTopics(newFilteredTopics);
    setHasMoreTopics(1 * TOPICS_PER_PAGE < topics.length);
  }, [tabValue, topics, searchQuery]);
  
  // Load more topics
  const loadMoreTopics = async () => {
    if (isLoadingMore || !hasMoreTopics) return;
    
    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const newFilteredTopics = getFilteredAndPaginatedTopics(topics, nextPage, tabValue);
      
      setFilteredTopics(newFilteredTopics);
      setCurrentPage(nextPage);
      setHasMoreTopics(nextPage * TOPICS_PER_PAGE < topics.length);
    } catch (error) {
      logger.error('Error loading more topics:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  // Search topics
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      // Reset to paginated view when clearing search
      setCurrentPage(1);
      const initialTopics = getFilteredAndPaginatedTopics(topics, 1, tabValue);
      setFilteredTopics(initialTopics);
      setHasMoreTopics(1 * TOPICS_PER_PAGE < topics.length);
      setIsSearching(false);
      setSelectedTopic(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const results = await searchTopics(searchQuery);
      setFilteredTopics(results);
      setSelectedTopic(null);
      // Disable "Load More" for search results (show all results)
      setHasMoreTopics(false);
      setCurrentPage(1);
    } catch (error) {
      logger.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle Enter key in search field
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };
  
  // Handle topic selection
  const handleTopicSelect = async (topic: ITopic) => {
    // Push state to browser history so back button works
    window.history.pushState({ topicSelected: true }, '', location.pathname);
    
    setSelectedTopic(topic);
    setActiveParent(topic.parentTopic || null);
    
    try {
      const topicData = await getTopicById(topic._id);
      setSelectedTopic(topicData);
      
      // Get explanations for this topic
      const explanationsData = await getTopicExplanations(topic._id);
      setExplanations(explanationsData);
      
      // Reset expanded explanations
      setExpandedExplanations(new Set([0])); // Expand first explanation by default
    } catch (error) {
      logger.error('Error loading topic details:', error);
    }
  };
  
  // Toggle explanation expansion
  const toggleExplanation = (index: number) => {
    const newSet = new Set(expandedExplanations);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setExpandedExplanations(newSet);
  };
  
  // Show subtopics of a parent
  const showSubtopics = (parentId: string) => {
    setFilteredTopics(topics.filter(t => t.parentTopic === parentId));
    setActiveParent(parentId);
    setSelectedTopic(null);
  };
  
  // Show root topics
  const showRootTopics = () => {
    // setFilteredTopics(topics.filter(t => !t.parentTopic));
    setFilteredTopics(topics); // Show all topics
    setActiveParent(null);
    setSelectedTopic(null);
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Calculate breadcrumbs
  const getBreadcrumbs = () => {
    if (!selectedTopic && !activeParent) return null;
    
    const breadcrumbs = [];
    
    // Home
    breadcrumbs.push(
      <Link 
        key="home" 
        underline="hover" 
        color="inherit" 
        component="button"
        onClick={showRootTopics}
      >
        Topics
      </Link>
    );
    
    // Parent topic (if any)
    if (activeParent) {
      const parentTopic = topics.find(t => t._id === activeParent);
      if (parentTopic) {
        breadcrumbs.push(
          <Link
            key={parentTopic._id}
            underline="hover"
            color="inherit"
            component="button"
            onClick={() => showSubtopics(parentTopic._id)}
          >
            {parentTopic.name}
          </Link>
        );
      }
    }
    
    // Current topic
    if (selectedTopic) {
      breadcrumbs.push(
        <Typography key="current" color="text.primary">
          {selectedTopic.name}
        </Typography>
      );
    }
    
    return (
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
        {breadcrumbs}
      </Breadcrumbs>
    );
  };
  

  
  // Handle challenge button click
  const handleChallengeClick = (explanation: ITopicExplanation, index: number) => {
    if (!isAuthenticated) {
      // Redirect to login or show login prompt
      window.location.href = '/login';
      return;
    }
    
    // Email verification is the gate (server does not use legacy admin approval for challenges)
    if (!user?.isEmailVerified) {
      alert('Please verify your email before challenging explanations.');
      return;
    }
    
    setSelectedExplanationForChallenge({ explanation, index });
    setChallengeModalOpen(true);
  };

  const handleChallengeModalClose = () => {
    setChallengeModalOpen(false);
    setSelectedExplanationForChallenge(null);
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {t('cubePage.title', 'CUBE - CUrrent Best Explanations')}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          {t('cubePage.subtitle', 'Discover the highest quality explanations on topics that matter')}
        </Typography>
        <Divider sx={{ mb: 4 }} />
      </Box>

      {/* Search Bar */}
      <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder={t('cubePage.searchPlaceholder', 'Search topics...')}
          variant="outlined"
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isSearching ? (
                  <CircularProgress size={24} />
                ) : (
                  <Button 
                    variant="contained" 
                    onClick={handleSearch}
                    disabled={isSearching}
                  >
                    {t('common:search', 'Search')}
                  </Button>
                )}
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Breadcrumbs navigation */}
      <Box sx={{ mb: 3 }}>
        {getBreadcrumbs()}
      </Box>

      {/* Main Content Area */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : selectedTopic ? (
        // Topic Detail View
        <Box>
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h4" gutterBottom>{selectedTopic.name}</Typography>
            {selectedTopic.description && (
              <Typography variant="body1" paragraph>
                {selectedTopic.description}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {selectedTopic.childTopics.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Sub-topics:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedTopic.childTopics.map(childId => {
                      const childTopic = topics.find(t => t._id === childId);
                      return childTopic ? (
                        <Chip 
                          key={childId}
                          label={childTopic.name}
                          onClick={() => handleTopicSelect(childTopic)}
                          clickable
                        />
                      ) : null;
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
          
          {/* Explanations */}
          <Typography variant="h5" gutterBottom>
            {t('cubePage.explanations', 'Explanations')}
          </Typography>
          
          {explanations.length > 0 ? (
            <Box>
              {(() => {
                // Sort explanations by status priority and survival metrics
                const sortedExplanations = [...explanations].sort((a, b) => {
                  // Status priority: active > under_challenge > challenged
                  const statusOrder = { 
                    'active': 3, 
                    'under_challenge': 2, 
                    'challenged': 1 
                  };
                  const aStatus = a.status || 'active';
                  const bStatus = b.status || 'active';
                  
                  if (aStatus !== bStatus) {
                    return (statusOrder[bStatus as keyof typeof statusOrder] || 0) - 
                           (statusOrder[aStatus as keyof typeof statusOrder] || 0);
                  }
                  
                  // For same status, sort by survival rate
                  const aSurvival = (a.challengesSurvived || 0) / Math.max(1, a.challengesReceived || 0);
                  const bSurvival = (b.challengesSurvived || 0) / Math.max(1, b.challengesReceived || 0);
                  return bSurvival - aSurvival;
                });
                
                // Group explanations by CUBE status
                const cubeExplanations = sortedExplanations.filter(e => 
                  (e.status || 'active') === 'active'
                );
                const underChallengeExplanations = sortedExplanations.filter(e => 
                  e.status === 'under_challenge'
                );
                const challengedExplanations = sortedExplanations.filter(e => 
                  e.status === 'challenged'
                );
                
                return (
                  <Box>
                    {/* CUBE Explanations Section */}
                    {cubeExplanations.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                            🟢 {t('cubePage.cubeExplanations', 'CUBE Explanations')}
                          </Typography>
                          <Chip 
                            label={cubeExplanations.length} 
                            size="small" 
                            color="success" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        {cubeExplanations.map((explanation, globalIndex) => (
                          <ExplanationCard 
                            key={`cube-${globalIndex}`}
                            explanation={explanation}
                            expanded={expandedExplanations.has(sortedExplanations.indexOf(explanation))}
                            toggleExpand={() => toggleExplanation(sortedExplanations.indexOf(explanation))}
                            onChallenge={() => handleChallengeClick(explanation, sortedExplanations.indexOf(explanation))}
                          />
                        ))}
                      </Box>
                    )}
                    
                    {/* Under Challenge Section */}
                    {underChallengeExplanations.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                            🟡 {t('cubePage.underChallenge', 'Under Challenge')}
                          </Typography>
                          <Chip 
                            label={underChallengeExplanations.length} 
                            size="small" 
                            color="warning" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        {underChallengeExplanations.map((explanation, globalIndex) => (
                          <ExplanationCard 
                            key={`challenge-${globalIndex}`}
                            explanation={explanation}
                            expanded={expandedExplanations.has(sortedExplanations.indexOf(explanation))}
                            toggleExpand={() => toggleExplanation(sortedExplanations.indexOf(explanation))}
                            onChallenge={() => handleChallengeClick(explanation, sortedExplanations.indexOf(explanation))}
                          />
                        ))}
                      </Box>
                    )}
                    
                    {/* Currently not in CUBE Section */}
                    {challengedExplanations.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
                            🔴 {t('cubePage.notInCube', 'Currently not in CUBE')}
                          </Typography>
                          <Chip 
                            label={challengedExplanations.length} 
                            size="small" 
                            color="error" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          These explanations have been successfully challenged and moved out of CUBE status.
                        </Typography>
                        {challengedExplanations.map((explanation, globalIndex) => (
                <ExplanationCard 
                            key={`challenged-${globalIndex}`}
                  explanation={explanation}
                            expanded={expandedExplanations.has(sortedExplanations.indexOf(explanation))}
                            toggleExpand={() => toggleExplanation(sortedExplanations.indexOf(explanation))}
                            onChallenge={() => handleChallengeClick(explanation, sortedExplanations.indexOf(explanation))}
                />
              ))}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography>No explanations available for this topic yet.</Typography>
            </Paper>
          )}
        </Box>
      ) : (
        // Topics List/Grid View
        <Box>
          <Box sx={{ mb: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="All Topics" />
              <Tab label="Recent" />
            </Tabs>
          </Box>
          
          {filteredTopics.length > 0 ? (
            <Box>
              <Grid container spacing={3}>
                {filteredTopics.map(topic => (
                  <Grid item xs={12} sm={6} md={4} key={topic._id}>
                    <TopicCard 
                      topic={topic} 
                      onClick={() => handleTopicSelect(topic)}
                    />
                  </Grid>
                ))}
              </Grid>
              
              {/* Pagination Info and Load More Button */}
              <Box sx={{ 
                mt: 4, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: 2 
              }}>
                {/* Show current count vs total */}
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredTopics.length} of {searchQuery.trim() ? filteredTopics.length : totalTopics} topics
                </Typography>
                
                {/* Load More Button - only show if not searching and has more topics */}
                {!searchQuery.trim() && hasMoreTopics && (
                  <Button
                    variant="outlined"
                    onClick={loadMoreTopics}
                    disabled={isLoadingMore}
                    sx={{ minWidth: 120 }}
                  >
                    {isLoadingMore ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading...
                      </>
                    ) : (
                      `Load More (${Math.min(TOPICS_PER_PAGE, totalTopics - filteredTopics.length)} more)`
                    )}
                  </Button>
                )}
                
                {/* Show when all topics are loaded */}
                {!searchQuery.trim() && !hasMoreTopics && totalTopics > TOPICS_PER_PAGE && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    All {totalTopics} topics loaded
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography>
                {searchQuery 
                  ? `No topics found matching "${searchQuery}"`
                  : 'No topics available'
                }
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Challenge Modal */}
      <ChallengeExplanationModal
        open={challengeModalOpen}
        onClose={handleChallengeModalClose}
        explanation={selectedExplanationForChallenge?.explanation || null}
        topicId={selectedTopic?._id || ''}
        explanationIndex={selectedExplanationForChallenge?.index || 0}
        topicName={selectedTopic?.name || ''}
        userPoints={user?.cubePoints || 0}
        canChallengeForFree={false} // TODO: Calculate based on user's contribution status
      />
    </Container>
  );
};

export default CubePage; 